import { useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Edit,
  Eye,
  FileText,
  XCircle,
  Loader2,
  CalendarDays,
} from 'lucide-react';

import { DriversFilterBar } from './components/DriversFilterBar';
import { DriversList } from './components/DriversList';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import type { RowAction } from '../../components/ui/RowActionsMenu';
import { ViewDriverModal } from './components/ViewDriverModal';
import { shellAssets } from '../../constants/assets';
import { dashboardRoutes } from '../../constants/routes';
import { useDrivers } from '../../hooks/useDrivers';
import type { DriverRecord } from '../../types';
import { useConfirmDialog } from '../../context/ConfirmDialogContext';
import { DriverBookingsModal } from './components/DriverBookingsModal';
import { useBookingsState } from '../../context/BookingsContext';

const currentUser = {
  name: 'Admin',
  email: 'Admin@123gmail.com',
  avatar: shellAssets.avatar,
};

const DriversPage = () => {
  const navigate = useNavigate();
  const { visible, filters, setFilters, removeDriver, isLoading } = useDrivers();
  const { confirm, showSuccess } = useConfirmDialog();
  const {
    records: bookingRecords,
    isLoading: bookingsLoading,
  } = useBookingsState();
  // "More Filters" acts as a disclosure for the filter row, which the design shows open.
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [viewingDriverId, setViewingDriverId] = useState<string | null>(null);
  const [
    viewBookingsDriver,
    setViewBookingsDriver,
  ] = useState<DriverRecord | null>(null);

  // Enrich drivers with accurate calculated income from all their assigned bookings
  const driversWithCalculatedIncome = useMemo(() => {
    return visible.map((driver) => {
      const assignedBookings = bookingRecords.filter((booking) => {
        const assignedDriverId = String(booking.driverId || '');
        if (
          assignedDriverId &&
          (assignedDriverId === String(driver.id) ||
            assignedDriverId === String(driver.code))
        ) {
          return true;
        }
        if (
          booking.driverCode &&
          booking.driverCode.trim().toLowerCase() ===
            driver.code.trim().toLowerCase()
        ) {
          return true;
        }
        if (
          !assignedDriverId &&
          booking.driverName &&
          booking.driverName.trim().toLowerCase() ===
            driver.name.trim().toLowerCase()
        ) {
          return true;
        }
        return false;
      });

      const bookingsIncome = assignedBookings.reduce((sum, b) => {
        const driverFare =
          Number(b.driverAmount) > 0
            ? Number(b.driverAmount)
            : b.driverAssigned
            ? Number(b.amountEur || 0)
            : 0;
        return sum + driverFare;
      }, 0);

      const resolvedIncome =
        bookingsIncome > 0
          ? bookingsIncome
          : Number(driver.incomeEur) || 0;

      return {
        ...driver,
        incomeEur: resolvedIncome,
      };
    });
  }, [visible, bookingRecords]);

  const activeViewBookingsDriver = useMemo(() => {
    if (!viewBookingsDriver) return null;
    return (
      driversWithCalculatedIncome.find(
        (d) => d.id === viewBookingsDriver.id
      ) || viewBookingsDriver
    );
  }, [driversWithCalculatedIncome, viewBookingsDriver]);

  const driverBookings = activeViewBookingsDriver
    ? bookingRecords.filter((booking) => {
        const assignedDriverId = String(booking.driverId || '');

        // Primary match: backend database driver ID
        if (
          assignedDriverId ===
          String(activeViewBookingsDriver.id)
        ) {
          return true;
        }

        // Also support public driver code (e.g. DRV-1001)
        if (
          (assignedDriverId &&
            assignedDriverId ===
              String(activeViewBookingsDriver.code)) ||
          (booking.driverCode &&
            booking.driverCode.trim().toLowerCase() ===
              activeViewBookingsDriver.code.trim().toLowerCase())
        ) {
          return true;
        }

        // Name fallback only when backend has not returned a driver ID
        if (!assignedDriverId && booking.driverName) {
          return (
            booking.driverName.trim().toLowerCase() ===
            activeViewBookingsDriver.name.trim().toLowerCase()
          );
        }

        return false;
      })
    : [];

  const buildActions = useCallback(
    (driver: DriverRecord): RowAction[] => [
      {
        id: 'edit',
        label: 'Edit Details',
        icon: Edit,
        onSelect: () => navigate(dashboardRoutes.driverEdit.replace(':id', driver.id)),
      },
      {
        id: 'view',
        label: 'View',
        icon: Eye,
        onSelect: () => setViewingDriverId(driver.id),
      },
      {
          id: 'bookings',
          label: 'View Bookings',
          icon: CalendarDays,
          onSelect: () =>
            setViewBookingsDriver(driver),
        },
        {
        id: 'invoice',
        label: 'Salary Invoice',
        icon: FileText,
        onSelect: () => toast('Salary invoice generation coming soon.', { icon: '??' }),
      },
      {
        id: 'remove',
        label: 'Delete Driver',
        danger: true,
        icon: XCircle,
        onSelect: async () => {
          const confirmed = await confirm({
            title: 'Delete Driver',
            message: `Are you sure you want to delete ${driver.name}? This action cannot be undone.`,
            confirmText: 'Delete Driver',
            cancelText: 'Cancel',
            variant: 'danger',
            icon: 'trash',
          });

          if (confirmed) {
            const ok = await removeDriver(driver.id);
            if (ok) {
              await showSuccess({
                title: 'Driver Deleted',
                message: `${driver.name} has been removed from the system.`,
              });
            } else {
              toast.error('Failed to delete driver');
            }
          }
        },
      },
    ],
    [navigate, removeDriver, confirm, showSuccess]
  );

  return (
    <DashboardLayout
      activeNavId="driver"
      contentGap={15}
      subtitle={
        <Breadcrumb
          items={[{ label: 'Dashboard', path: dashboardRoutes.overview }, { label: 'Drivers' }]}
        />
      }
      title="Drivers"
      user={currentUser}
    >
    <div className="flex min-h-0 flex-col gap-[13px] lg:h-[calc(100dvh-175px)] lg:overflow-hidden">
  {/* Fixed driver filters */}
  {filtersOpen && (
    <div className="shrink-0">
      <DriversFilterBar
        filters={filters}
        onAddDriver={() => navigate(dashboardRoutes.driverNew)}
        onChange={setFilters}
      />
    </div>
  )}

  {isLoading ? (
    <div className="flex min-h-0 flex-1 items-center justify-center rounded-[16px] bg-white py-20">
      <Loader2
        className="animate-spin text-[#FF6A1A]"
        size={32}
      />
    </div>
  ) : (
    <DriversList
      buildActions={buildActions}
      count={driversWithCalculatedIncome.length}
      drivers={driversWithCalculatedIncome}
      filtersOpen={filtersOpen}
      onToggleFilters={() => setFiltersOpen((open) => !open)}
    />
  )}
</div>

      <ViewDriverModal
        driver={
          driversWithCalculatedIncome.find((d) => d.id === viewingDriverId) ||
          null
        }
        isOpen={Boolean(viewingDriverId)}
        onClose={() => setViewingDriverId(null)}
      />
      <DriverBookingsModal
        driver={activeViewBookingsDriver}
        bookings={driverBookings}
        isOpen={Boolean(activeViewBookingsDriver)}
        isLoading={bookingsLoading}
        onClose={() => setViewBookingsDriver(null)}
      />
    </DashboardLayout>
  );
};

export default DriversPage;